import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../components/TenantContext.jsx';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty, StatusDot } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Building2, ChevronRight, Plus } from 'lucide-react';

export default function TenantsListPage() {
    const { t } = useTranslation('tenant');
    const { tenants, hostId, loading, reload } = useTenant();
    // 每次進入本頁靜默重抓：SDX500 provision（東訊後台改 AI 分機）會從外部改 Firestore，
    // context 只在登入時載一次 → 卡片上的「分機號碼」是舊的、切左欄回來也不更新（0803 東訊回報）。
    useEffect(() => {
        reload({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // 只顯示切換器目前選的租戶（跟其他頁一致：選哪個就只看哪個的設定）
    const shown = tenants.filter((tt) => tt.hostId === hostId);

    return (
        <div>
            <PageHeader
                eyebrow={t('list.eyebrow')}
                title={t('list.title')}
                description={t('list.description')}
                actions={
                    <Button variant="ghost" onClick={reload} size="sm">
                        {t('common:actions.refresh')}
                    </Button>
                }
            />

            {loading ? (
                <Card>
                    <div className="text-center text-ink-400 text-sm py-6">{t('common:state.loading')}</div>
                </Card>
            ) : shown.length === 0 ? (
                <Card>
                    <Empty
                        icon={Building2}
                        title={t('list.empty.title')}
                        description={t('list.empty.description')}
                        action={
                            <Button variant="accent" disabled>
                                <Plus size={16} /> {t('list.empty.add')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className="grid gap-3">
                    {shown.map((tt) => (
                        <TenantCard key={tt.hostId} tenant={tt} />
                    ))}
                </div>
            )}
        </div>
    );
}

function TenantCard({ tenant }) {
    const { t } = useTranslation('tenant');
    const ipPhones = tenant.ipPhones || [];
    const outboundCfg = tenant.outboundConfig || {};
    // 實際最大並發 = min(設定的最大外播數, IP phone 分機數)
    //   每個 ext 同時只能 1 通；分機數量是 hard cap，設定再大也沒用
    const configMax = Number(outboundCfg.maxTrunks) > 0 ? Number(outboundCfg.maxTrunks) : null;
    const phoneCount = ipPhones.length;
    const effectiveMax = configMax != null ? Math.min(configMax, phoneCount) : phoneCount;
    const maxLabel = effectiveMax > 0
        ? (configMax != null && configMax !== effectiveMax
            ? t('card.maxLines.capped', { effective: effectiveMax, config: configMax, phones: phoneCount })
            : t('card.maxLines.value', { n: effectiveMax }))
        : t('card.notConfigured');
    const dialPrefix = typeof outboundCfg.dialPrefix === 'string' ? outboundCfg.dialPrefix : '';
    return (
        <Link
            to={`/tenants/${encodeURIComponent(tenant.hostId)}`}
            className="block group"
        >
            <Card className="hover:border-accent-300 transition group-hover:shadow-md">
                <div className="flex items-start gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                            <h3 className="heading-display text-xl font-semibold text-ink-900">
                                {tenant.displayName || tenant.hostId}
                            </h3>
                            <code className="font-mono text-xs text-ink-400 bg-cream-100 px-2 py-0.5 rounded">
                                {tenant.hostId}
                            </code>
                            <StatusDot
                                kind={tenant.enabled === false ? 'idle' : 'success'}
                                label={tenant.enabled === false ? t('status.disabled') : t('status.enabled')}
                            />
                        </div>
                        <div className="text-sm text-ink-500 mb-4">
                            {tenant.pbx?.model || tenant.pbxModel || t('card.pbxNotConfigured')}
                            {tenant.pbx?.publicIp && (
                                <>
                                    <span className="mx-2 text-ink-300">·</span>
                                    <code className="font-mono">{tenant.pbx.publicIp}</code>
                                </>
                            )}
                            {tenant.pbx?.ipPhoneRegisterPort && (
                                <>
                                    <span className="mx-2 text-ink-300">·</span>
                                    {t('card.registerPort')} <code className="font-mono">{tenant.pbx.ipPhoneRegisterPort}</code>
                                </>
                            )}
                        </div>
                        <div className="grid grid-cols-3 gap-x-8 gap-y-2 text-xs">
                            <Stat
                                label={t('card.stat.maxLines')}
                                value={maxLabel}
                            />
                            <Stat
                                label={t('card.stat.dialPrefix')}
                                value={dialPrefix ? <code className="font-mono">{dialPrefix}</code> : t('card.noPrefix')}
                            />
                            <Stat
                                label={t('card.stat.extensions')}
                                value={ipPhones.map((p) => p.ext).join('、') || t('card.notConfigured')}
                            />
                        </div>
                    </div>
                    <ChevronRight
                        size={20}
                        className="text-ink-300 group-hover:text-accent-500 transition shrink-0 mt-1"
                    />
                </div>
            </Card>
        </Link>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                {label}
            </div>
            <div className="text-sm text-ink-700 font-medium tnum mt-0.5">{value}</div>
        </div>
    );
}
